import { useCallback, useState } from 'react';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { MatterDocsWithCasesFilters } from '../../../../../../../backend/src/matter/doc/dto/doc-with-cases.response.dto';
import { DocsTable } from '../docs/components/docs-table';
import { useGetMatterDocsWithCases } from '@/api/queries/useGetMatterDocsWithCases';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

export function DocumentManagement() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'file.name', desc: false }]);
  const [filters, setFilters] = useState<MatterDocsWithCasesFilters>({});

  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const debouncedSetSearchQuery = useDebouncedCallback((value: string) => {
    setDebouncedSearchQuery(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, 300);

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setLocalSearchQuery(query);
      debouncedSetSearchQuery(query);
    },
    [debouncedSetSearchQuery],
  );

  const handleFiltersChange = useCallback((newFilters: MatterDocsWithCasesFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    setSorting(updaterOrValue);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Convert sorting state to API params
  const sortBy = sorting.length > 0 ? (sorting?.[0]?.id?.split('.').pop() ?? undefined) : undefined;
  const sortOrder = sorting.length > 0 ? (sorting?.[0]?.desc ? 'DESC' : 'ASC') : undefined;

  const { data: matter } = useGetMatterV2(matterId);

  // User can delete documents if they are the matter creator OR have the MatterDeleteDocument permission
  const isMatterCreator = matter?.createdById === user?.id;
  const canDeleteDocuments = isMatterCreator || hasPermission(Permission.MatterDeleteDocument);

  const { data, isLoading } = useGetMatterDocsWithCases(matterId, {
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filters: { ...filters, searchQuery: debouncedSearchQuery || undefined },
    sortBy,
    sortOrder,
  });

  return (
    <>
      <SettingsHeader
        title="Document Management"
        subheading={
          <p className="text-sm text-gray-500">
            View and manage all documents uploaded to {matter?.name ?? 'this matter'}.
          </p>
        }
        action={
          <Button asChild variant="primary">
            <Link to={`/matters/${matterId}/docs/upload`}>
              <Icon className="lucide--file-up" />
              <span>Upload Documents</span>
            </Link>
          </Button>
        }
      />

      <DocsTable
        docs={data?.docs}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        pageCount={data?.pageCount ?? 0}
        totalCount={data?.totalCount ?? 0}
        searchQuery={localSearchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        canDeleteDocuments={canDeleteDocuments}
      />
    </>
  );
}
